"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useBrand } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { SidebarStore } from "@/app/stores/SidebarStore";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Globals } from "@/app/vars/Globals";
import { Routes } from "@/app/vars/Routes";
import styles from "./Logo.module.css";

type Props = {
  max?: boolean;
  light?: boolean;
  blinker?: boolean;
  min?: boolean;
  ignoreBrand?: boolean;
  nonInteractive?: boolean;
  className?: string;
  onClick?: () => void;
};

export default function Logo({
  max = false,
  light = false,
  blinker = false,
  min = false,
  ignoreBrand = false,
  nonInteractive = false,
  className,
  onClick,
}: Props) {
  const router = useRouter();

  const { closeSidebarOnMobile } = SidebarStore();
  const brand = useBrand();
  const effectiveBrand = ignoreBrand ? null : brand;
  const brandLogoUrl = effectiveBrand?.logoUrl;
  const brandName = effectiveBrand?.name || Globals.COMPANY.NAME;

  const classNames = UStyles.classNames(
    styles.logo,
    max ? styles.max : "",
    light ? styles.light : "",
    blinker ? styles.blinker : "",
    min ? styles.min : "",
    className,
  );

  const content = (
    <>
      {brandLogoUrl ? (
        <Image
          src={brandLogoUrl}
          alt={brandName}
          width={0}
          height={0}
          sizes="200px"
          className={styles.brandLogo}
          style={{
            height: min ? 30 : max ? 64 : 38,
            width: "auto",
            maxWidth: min ? 40 : max ? 240 : 160,
            objectFit: "contain",
          }}
        />
      ) : (
        <svg
          id="Calque_8"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={min ? "0 0 450 516.17" : "0 0 1227.79 516.17"}
        >
          <g className={styles.m}>
            <path
              className={styles.cls3}
              d="M96.32,286.48l11.95,15.41,35.5,45.72,13.85,17.83c2.27,3.33,4.67,5.64,6.95,7.56l1.71,1.34c.56.38,1.1.72,1.65,1.08,1.12.78,2.13,1.23,3.18,1.8,4.1,2.03,7.52,2.8,10.41,3.25.69.09,1.48.23,2.1.27.61.05,1.4.1,2.25.12,1.74.04,3.93-.06,6.3-.43,2.37-.38,4.89-1.04,7.32-1.98,2.43-.94,4.79-2.15,6.87-3.55,2.09-1.38,3.92-2.92,5.33-4.33,1.46-1.44,2.39-2.58,3.2-3.62,1.67-2.28,3.43-4.97,4.93-8.93.35-1.05.75-1.96,1.02-3.2l.45-1.76c.14-.61.23-1.31.34-1.96.26-1.27.3-2.83.41-4.31.03-.77-.02-1.63-.03-2.44-.02-.42,0-.8-.05-1.25l-.15-1.34-12.13-79.91s0,0,0,0l53.51,103.6,2.84,4.56.35.57c.12.19.26.31.38.46l.77.88,1.55,1.76c.52.56,1.03,1.23,1.55,1.71l1.55,1.26c1.03.82,2.07,1.73,3.08,2.49l2.88,1.67,1.44.84c.48.3.97.45,1.46.68.5.21.94.43,1.5.63l1.65.61c.52.22,1.17.36,1.76.54.62.16,1.18.36,1.85.48,2.54.62,5.33.87,8.15.97,2.84-.08,5.73-.26,8.52-.95,2.78-.64,5.49-1.59,7.94-2.76.65-.26,1.18-.62,1.78-.93.58-.32,1.18-.63,1.69-.98l1.59-1.01c.5-.34.94-.71,1.42-1.06.99-.72,1.71-1.4,2.54-2.09.79-.74,1.51-1.61,2.27-2.41l1.13-1.22.56-.61.55-.82,2.21-3.33.55-.83c.17-.33.32-.71.48-1.06l.96-2.14.96-2.14c.18-.32.25-.83.38-1.24l.34-1.3.69-2.6c.11-.44.24-.85.33-1.31l.25-1.4,1.01-5.61,6.22-34.53,11.25-62.46s0,0,0,0l20.79,73.71,6.13,21.73,46.22-13.04-6.13-21.73-25.6-90.75-7.1-25.18-3.55-12.59-1.01-2.85c-.38-.8-.79-1.51-1.19-2.27l-1.2-2.25-.3-.56c-.1-.19-.2-.38-.31-.5l-.63-.82-2.53-3.27-.32-.41c-.11-.13-.2-.2-.31-.3l-.61-.56-1.22-1.12-1.22-1.12-.61-.56c-.2-.17-.42-.41-.59-.5l-2.16-1.4-2.16-1.4c-.38-.27-.66-.36-.98-.52l-.94-.44c-1.1-.54-2.81-1.3-5.07-1.95l-1.77-.47-1.93-.44-2.06-.28-2.15-.2c-2.9-.15-5.92.02-8.74.57-2.82.49-5.43,1.41-7.5,2.27-.51.22-1.05.45-1.45.66-.41.21-.91.45-1.23.63l-1.02.55-.9.53c-.29.15-.63.43-.95.66l-.98.74-1.97,1.47c-.33.27-.64.44-1,.78l-1.09,1.13-2.18,2.26-.54.57c-.18.24-.37.53-.55.8l-1.11,1.63-1.11,1.63-.56.81c-.17.35-.34.73-.51,1.1l-2.02,4.44-1.53,5.73-21.09,117.02-1.86,10.34h0s-34.16-66.14-34.16-66.14l-36.79-71.24-5.19-10.04-2.59-5.02c-.43-.82-.86-1.72-1.3-2.47l-1.29-2.05c-1.76-3.14-3.35-4.43-4.99-6.34l-.61-.68c-.2-.2-.37-.3-.56-.45l-1.11-.85-2.21-1.71c-.78-.67-1.37-.9-2.01-1.25l-1.89-.99-1.89-.99-1.54-.5-3.09-1c-.56-.21-.92-.24-1.32-.3l-1.2-.18-2.39-.37c-.38-.05-.84-.15-1.15-.15l-.57-.03-.69-.02c-2.15-.09-7.05.14-11.44,1.67-2.23.69-4.35,1.71-6.14,2.68-1.77.98-3.19,2.04-4.03,2.66l-.86.67-.86.84-1.71,1.67-.86.84-.43.42c-.15.17-.32.42-.47.63l-1.91,2.63-.96,1.31c-.32.6-.63,1.28-.95,1.92l-.95,1.94-.47.97c-.08.17-.16.3-.23.51l-.19.68-.78,2.73-.39,1.36-.19.68c-.07.21-.07.59-.12.87l-.38,3.68-.1.92-.05.46c0,.17.02.39.03.59l.16,2.42c.03.41.04.79.09,1.23l.21,1.4.43,2.79.85,5.58,1.71,11.17,15.08,98.77s0,0,0,0l-20.55-26.5-11.95-15.41-37.95,29.43Z"
            />
            <path
              className={styles.cls1}
              d="M130.01,290.74c-10.48,8.13-25.57,6.22-33.7-4.26-8.13-10.48-6.22-25.57,4.26-33.7,10.48-8.13,25.57-6.22,33.7,4.26,8.13,10.48,6.22,25.57-4.26,33.7Z"
            />
            <path
              className={styles.cls1}
              d="M408.17,362.8c-12.77,3.6-26.03-3.83-29.63-16.59s3.83-26.03,16.59-29.63,26.03,3.83,29.63,16.59-3.83,26.03-16.59,29.63Z"
            />
          </g>
          {!min && (
            <>
              <path
                className={styles.cls3}
                d="M972.39,341.59c0,6.59-2.92,12.24-8.46,16.32-4.84,3.57-10.91,5.38-18.06,5.38h-.53c-9.57,0-18.21-2.67-25.68-7.95-7.25-5.12-12.97-12.15-16.96-20.87-3.87-8.46-5.83-18.23-5.83-29.03v-146.18c0-6.11,2.13-11.47,6.18-15.52,4.03-4.03,9.4-6.18,15.51-6.18s11.47,2.15,15.52,6.18c4.05,4.05,6.18,9.41,6.18,15.52v146.18c0,5.09.88,9.17,2.61,12.15,1.34,2.3,2.16,2.3,2.46,2.3h6.69c5.92,0,11.08,2.25,14.9,6.51,3.63,4.08,5.46,9.19,5.46,15.19Z"
              />
              <path
                className={styles.cls3}
                d="M851.23,143.74c-4.05-4.03-9.51-6.18-15.78-6.18s-11.42,2.1-15.55,6.09c-4.2,4.05-6.42,9.55-6.42,15.88v53.24c-3.29-2.33-6.8-4.4-10.52-6.23-10.03-4.91-21.08-7.4-32.84-7.4-14.57,0-27.85,3.72-39.5,11.05-11.53,7.27-20.75,17.23-27.4,29.62-6.6,12.32-9.94,26.39-9.94,41.8s3.66,29.73,10.88,42.19c7.22,12.45,17.15,22.43,29.51,29.65,12.38,7.24,26.45,10.9,41.8,10.9s29.39-3.67,41.69-10.91c12.27-7.22,22.14-17.18,29.35-29.63,7.24-12.48,10.9-26.67,10.9-42.19v-122.08c0-6.27-2.13-11.73-6.18-15.79ZM809.38,303.4c-3.43,6.41-7.98,11.27-13.89,14.9-5.85,3.6-12.41,5.34-20.02,5.34s-13.96-1.75-19.92-5.37c-6.01-3.63-10.64-8.52-14.15-14.95-3.55-6.48-5.27-13.59-5.27-21.72s1.71-15.34,5.23-21.64c3.51-6.27,8.15-11.09,14.19-14.75,5.95-3.61,12.47-5.37,19.92-5.37s14.16,1.75,20.02,5.34c5.94,3.64,10.49,8.46,13.92,14.72,3.42,6.23,5.14,13.53,5.14,21.7s-1.74,15.37-5.17,21.79Z"
              />
              <g>
                <path
                  className={styles.cls3}
                  d="M673.99,163.55c0,6.98-2.66,13.31-7.66,18.32-5,5.02-11.35,7.66-18.33,7.66s-13.31-2.64-18.33-7.66c-5-5-7.65-11.33-7.65-18.32s2.64-13.33,7.66-18.33c5-5.02,11.33-7.66,18.32-7.66s13.33,2.64,18.33,7.66c5,5,7.66,11.33,7.66,18.33Z"
                />
                <path
                  className={styles.cls3}
                  d="M669.97,222.17v119.14c0,6.06-2.1,11.42-6.09,15.55-4.05,4.2-9.55,6.42-15.88,6.42s-11.52-2.19-15.66-6.33c-4.13-4.11-6.3-9.52-6.3-15.64v-119.14c0-6.33,2.22-11.82,6.41-15.88,4.13-3.97,9.51-6.08,15.55-6.08s11.73,2.13,15.78,6.18c4.05,4.05,6.2,9.51,6.2,15.78Z"
                />
              </g>
              <path
                className={styles.cls3}
                d="M590.45,298.11c2.45-.95,4.67-2.37,6.6-4.22,3.94-3.79,6.03-8.78,6.03-14.43,0-15.29-2.95-29.09-8.73-40.97-5.94-12.2-14.69-21.91-26.01-28.89-11.29-6.94-24.83-10.46-40.23-10.46s-29.53,3.57-41.68,10.62c-12.17,7.1-21.81,16.99-28.65,29.41-6.82,12.36-10.28,26.73-10.28,42.71s3.67,30.22,10.91,42.6c7.25,12.45,17.42,22.32,30.21,29.33,12.69,7,27.34,10.53,43.49,10.53,9.11,0,18.69-1.68,28.47-4.97,9.87-3.31,18.33-7.74,25.04-13.06,6.62-5.08,7.75-11.14,7.54-15.37-.21-3.79-1.72-9.4-7.75-14.7-3.84-3.26-8.6-4.9-13.81-4.79-4.81.15-9.29,1.72-13.42,4.73-2.89,2.16-6.86,4.16-11.85,5.92-4.87,1.72-9.64,2.6-14.22,2.6-8.78,0-16.29-1.83-22.94-5.61-6.62-3.76-11.65-8.72-15.38-15.16-.82-1.4-1.56-2.87-2.19-4.38h90.85c2.84,0,5.53-.5,7.99-1.45ZM491.95,261.52c.33-.82.68-1.62,1.06-2.4,3.1-6.38,7.47-11.24,13.36-14.87,5.86-3.63,13.18-5.46,21.75-5.46,7.33,0,13.27,1.56,18.18,4.79,5,3.28,8.78,7.66,11.53,13.37.7,1.47,1.33,2.99,1.87,4.56h-67.75Z"
              />
              <g>
                <path
                  className={styles.cls3}
                  d="M1125.32,228.97h0c-.03.08-.05.14-.08.18l-9.16,20.67-64.11,144.47-9.23,20.84c-4.93,11.09-17.91,16.11-29.02,11.18-11.09-4.93-16.09-17.92-11.18-29.01l9.25-20.84,16.29-36.71-57.76-109.68c-2.96-5.67-3.28-11.52-.95-16.94,2.18-5.06,6.15-8.93,11.49-11.2,5.35-2.25,10.84-2.43,15.85-.5,4.99,1.9,8.95,5.68,11.45,10.93l40.64,80.68,27.08-61.05,9.25-20.84v-.02l40.18,17.83Z"
                />
                <g>
                  <path
                    className={styles.cls2}
                    d="M1125.32,228.97h0c-.03.08-.05.14-.08.18-.8,1.75-1.8,3.35-2.98,4.79-6.08,7.51-16.68,10.34-25.95,6.23-9.25-4.1-14.27-13.8-12.83-23.32.27-1.92.82-3.84,1.65-5.7v-.02l.8.35,38.64,17.15.74.33Z"
                  />
                  <path
                    className={styles.cls2}
                    d="M1125.32,228.97h0c-.03.08-.05.14-.08.18-.8,1.75-1.8,3.35-2.98,4.79-6.08,7.51-16.68,10.34-25.95,6.23-9.25-4.1-14.27-13.8-12.83-23.32.27-1.92.82-3.84,1.65-5.7v-.02c4.93-11.09,17.91-16.09,29-11.17,11.11,4.93,16.11,17.89,11.18,29Z"
                  />
                </g>
              </g>
            </>
          )}
        </svg>
      )}
    </>
  );

  if (nonInteractive) {
    return (
      <span className={classNames} role="img" aria-label={brandName}>
        {content}
      </span>
    );
  }

  return (
    <button
      className={classNames}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
          return;
        }
        closeSidebarOnMobile();
        router.push(URLs.pathOf(Routes.HOME));
      }}
      aria-label={brandName}
    >
      {content}
    </button>
  );
}
